import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import styled from '@emotion/styled';
import { css as emotionCSS } from '@emotion/react';
import { space } from 'styled-system';
import css from '@styled-system/css';
import { Box } from '../box';
import { Error } from '../error';
import { Helper } from '../helper';
const getWrapperBorderColor = ({ theme, disabled, error, }) => {
    if (disabled)
        return theme.input.disabledColor;
    if (error)
        return theme.input.errorColor;
    return theme.input.borderColor;
};
const getWrapperFocusedBorderColor = ({ theme, disabled, error, }) => {
    if (disabled)
        return theme.input.disabledColor;
    if (error)
        return theme.input.errorColor;
    return theme.input.borderFocusColor;
};
const getWrapperHoveredBorderColor = ({ theme, disabled, error, }) => {
    if (disabled)
        return theme.input.disabledColor;
    if (error)
        return theme.input.errorColor;
    return theme.input.borderHoverColor;
};
const Wrapper = styled.div `
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  border: 1px solid ${(props) => getWrapperBorderColor(props)};
  border-radius: 8px;
  height: 64px;
  background-color: ${(props) => props.theme.input.backgroundColor};
  transition: box-shadow 300ms;
  &:focus-within {
    border-color: ${(props) => getWrapperFocusedBorderColor(props)};
    box-shadow: 0px 4px 4px rgba(117, 117, 117, 0.25);
  }

  &:hover {
    border-color: ${(props) => getWrapperHoveredBorderColor(props)};
  }

  ${({ isDropDownOpen, theme }) => isDropDownOpen &&
    emotionCSS `
    border-color: ${theme.input.borderHoverColor};
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
    border-bottom: 1px solid transparent;
    z-index: ${theme.zIndices.interaction};
    &:hover {
      border-color: ${theme.input.borderHoverColor};
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
      border-bottom: 1px solid transparent;
    }
    &:focus-within {
      border-color: ${theme.input.borderHoverColor};
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
      border-bottom: 1px solid transparent;
    }
    `}
  ${space};
`;
const getFocusedLabelColor = ({ disabled, error, theme }) => {
    if (disabled)
        return theme.input.disabledColor;
    if (error)
        return theme.input.errorColor;
    return theme.input.focusedColor;
};
const getLabelColor = ({ disabled, error, theme }) => {
    if (disabled)
        return theme.input.disabledColor;
    if (error)
        return theme.input.errorColor;
    return theme.input.labelColor;
};
/**
 * The default placeholder (`placeholder="&nbsp;"`) is used to inform the css when there's a value in the input.
 * For inputs that have actual placeholders we need to treat it as if they've always
 * got either a placeholder or a value so that the label shows up in the top left.
 */
const LabelBaseStyles = css({
    fontSize: 'm',
    fontWeight: 'regular',
    'input:focus-within ~ &, input:focus-within:not(:placeholder-shown) ~ &': {
        fontSize: 'xs',
        fontWeight: 'semiBold',
    },
    'input:-webkit-autofill:not(:focus-within) ~ &, input:focus:not(:focus-within) ~ &, input:not(:focus-within):not(:placeholder-shown) ~ &': {
        fontSize: 'xs',
        fontWeight: 'regular',
    },
    /** inputs with placeholders have labels which are always on top left, e.g. `DateInput`
     * those inputs use class `input-with-placeholder` on the Input */
    'input.input-with-placeholder ~ &': {
        fontSize: 'xs',
        transform: `translate3d(0, -10px, 0)`,
    },
    whiteSpace: 'nowrap',
});
const Label = styled.label `
  ${LabelBaseStyles}
  color: ${(props) => getLabelColor(props)};
  font-family: ${({ theme }) => theme.fonts.body};
  position: absolute;
  pointer-events: none;
  top: calc(64px / 3);
  transition: transform 0.1s ease-in-out;
  input:-webkit-autofill ~ &, input:focus-within ~ &, input:not(:placeholder-shown) ~ & {
    transform: translate3d(0, -10px, 0);
    color: ${(props) => getFocusedLabelColor(props)};
  },
  input:not(:focus-within) ~ &, input:not(:focus-within):not(:placeholder-shown) ~ & {
    color: ${(props) => getLabelColor(props)};
  },
`;
const InputWrapper = styled.div `
  position: relative;
  flex-grow: 1;
  width: 100%;
  height: 100%;
`;
const StyledInputBaseStyles = css({
    fontSize: 'm',
    fontWeight: 'regular',
    lineHeight: 'm',
});
const StyledInput = styled.input `
  ${StyledInputBaseStyles}
  display: block;
  width: 100%;
  height: 100%;
  color: ${(props) => props.theme.input.color};
  background-color: inherit;
  border: none;
  outline: none;

  /*
    Add specificity since there is some competition for padding properties.
  */
  &,
  &[type='text'],
  &[type='tel'],
  &[type='password'],
  &[type='email'] {
    padding: 0;
    padding-top: calc(64px / 3);
  }

  box-sizing: border-box;
  font-family: ${({ theme }) => theme.fonts.body};

  /* Change text in autofill textbox*/
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    transition: background-color 5000s ease-in-out 0s;
    -webkit-box-shadow: 0 0 0px 1000px white inset;
    background-color: white !important;
    background-clip: content-box !important;
  }

  &::placeholder {
    color: ${({ theme }) => theme.input.labelColor};
  }

  &:disabled {
    color: ${({ theme }) => theme.input.disabledColor};
  }
`;
const Slot = styled.div `
  white-space: nowrap;
  vertical-align: middle;
  ${space}
`;
export const Input = ({ label, error, helper, leftSlot, rightSlot, disabled, inputRef, isDropDownOpen = false, dataCy, ...props }) => {
    const cyName = useMemo(() => {
        if (dataCy) {
            return dataCy;
        }
        if (props.id) {
            return props.id;
        }
        if (props.name) {
            return props.name;
        }
        return 'unknown';
    }, [dataCy, props.id, props.name]);
    return (_jsxs(_Fragment, { children: [_jsxs(Wrapper, { paddingX: "s", disabled: disabled, error: error, isDropDownOpen: isDropDownOpen, children: [leftSlot && _jsx(Slot, { marginRight: "xs", children: leftSlot }), _jsxs(InputWrapper, { children: [_jsx(StyledInput, { ref: inputRef, type: "text", placeholder: "\u00A0", disabled: disabled, "data-cy": cyName, "data-testid": `input-label-${cyName}`, ...props }), _jsx(Label, { disabled: disabled, error: error, htmlFor: props === null || props === void 0 ? void 0 : props.id, "data-cy": `input-label-${cyName}`, "data-testid": `input-label-${cyName}`, children: label })] }), rightSlot && _jsx(Slot, { marginLeft: "xs", children: rightSlot })] }), (error || helper) && (_jsx(Box, { minHeight: "22px", marginTop: "xxs", children: error ? (_jsx(Error, { as: "span", variant: "xs", "data-cy": `input-error-${cyName}`, "data-testid": `input-error-${cyName}`, children: error })) : typeof helper === 'string' ? (_jsx(Helper, { color: props === null || props === void 0 ? void 0 : props.helperColor, "data-cy": `input-helper-${cyName}`, "data-testid": `input-helper-${cyName}`, children: helper })) : (helper !== null && helper !== void 0 ? helper : null) }))] }));
};
//# sourceMappingURL=data:application/json;base64,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